import { cn } from '@/lib/utils';
import React from 'react';

type FeatureType = {
	title: string;
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	description: React.ReactNode;
	number?: number;
	suffix?: string;
	color?: string;
	customText?: string;
};

type FeatureCardProps = React.ComponentProps<'div'> & {
	feature: FeatureType;
	counter?: number;
	index?: number;
	compact?: boolean;
};

export function FeatureCard({ feature, counter: _counter, className, index: _index, compact, ...props }: FeatureCardProps) {
	const p = genRandomPattern();

	return (
		<div className={cn(
			'relative overflow-hidden bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 group text-left rounded-lg w-full',
			compact ? 'p-3 sm:p-4' : 'p-4 sm:p-5 md:p-6',
			className
		)} {...props}>
			<div className="pointer-events-none absolute top-0 left-1/2 -mt-2 -ml-20 h-full w-full [mask-image:linear-gradient(white,transparent)]">
				<div className="from-foreground/5 to-foreground/1 absolute inset-0 bg-gradient-to-r [mask-image:radial-gradient(farthest-side_at_top,white,transparent)] opacity-40">
					<GridPattern
						width={20}
						height={20}
						x="-12"
						y="4"
						squares={p}
						className="fill-foreground/5 stroke-foreground/25 absolute inset-0 h-full w-full mix-blend-overlay [animation-duration:15s]"
					/>
				</div>
			</div>
			
			<div className={cn("relative z-10", compact ? "mb-2 sm:mb-3" : "mb-4 sm:mb-6")}>
				<div className={cn(
					feature.color || 'bg-primary',
					'rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300',
					compact ? 'w-10 h-10 sm:w-12 sm:h-12 mb-2 sm:mb-3' : 'w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4 sm:rounded-2xl'
				)}>
					<feature.icon className={cn("text-white", compact ? "h-5 w-5 sm:h-6 sm:w-6" : "h-6 w-6 sm:h-8 sm:w-8")} strokeWidth={1} aria-hidden />
				</div>
				<h3 className={cn("font-bold text-foreground", compact ? "text-base sm:text-lg mb-1 sm:mb-2" : "text-lg sm:text-xl mb-2 sm:mb-3")}>{feature.title}</h3>
				<p className={cn("text-muted-foreground leading-relaxed relative z-20", compact ? "text-xs sm:text-sm" : "text-sm sm:text-base")}>{feature.description}</p>
			</div>
		</div>
	);
}

function GridPattern({
	width,
	height,
	x,
	y,
	squares,
	...props
}: React.ComponentProps<'svg'> & { width: number; height: number; x: string; y: string; squares?: number[][] }) {
	const patternId = React.useId();

	return (
		<svg aria-hidden="true" {...props}>
			<defs>
				<pattern id={patternId} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
					<path d={`M.5 ${height}V.5H${width}`} fill="none" />
				</pattern>
			</defs>
			<rect width="100%" height="100%" strokeWidth={0} fill={`url(#${patternId})`} />
			{squares && (
				<svg x={x} y={y} className="overflow-visible animate-pulse [animation-duration:12s] opacity-40">
					{squares.map(([x, y], index) => (
						<rect strokeWidth="0" key={index} width={width + 1} height={height + 1} x={x * width} y={y * height} />
					))}
				</svg>
			)}
		</svg>
	);
}

function genRandomPattern(length?: number): number[][] {
	length = length ?? 5;
	return Array.from({ length }, () => [
		Math.floor(Math.random() * 4) + 7, // random x between 7 and 10
		Math.floor(Math.random() * 6) + 1, // random y between 1 and 6
	]);
}
